import { useState } from 'react';
import { AssignedInternalUserResponseDto } from '../../../../lib/interfaces/organisation';
import { UserSelectableEntityResponseDto } from '../../../../lib/interfaces/user';
import { Button } from '../../../../ui/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../ui/components/ui/select';

/** Tints of the two brand colors - keeps the allocation bar on brand without introducing new hues. */
const MEMBER_COLORS = ['#5FD5DE', '#C0DE60', '#3FA9B3', '#9BB84A', '#7FC9D6', '#D3E88C'];

const getMemberColor = (index: number) => MEMBER_COLORS[index % MEMBER_COLORS.length];

const getInitials = (fullName: string) =>
  fullName
    .split(' ')
    .map((part) => part.charAt(0))
    .join('')
    .slice(0, 2)
    .toUpperCase();

export interface AssignmentMembersEditorProps {
  allowedJobsCount: number;
  /** Server state. Changing it remounts the editor via key, which resets the draft below. */
  initialMembers: AssignedInternalUserResponseDto[];
  selectableUsers: UserSelectableEntityResponseDto[];
  isSaving: boolean;
  onSave: (members: AssignedInternalUserResponseDto[]) => void;
}

const AssignmentMembersEditor = ({ allowedJobsCount, initialMembers, selectableUsers, isSaving, onSave }: AssignmentMembersEditorProps) => {
  const [members, setMembers] = useState<AssignedInternalUserResponseDto[]>(initialMembers);

  const assignedCapacity = members.reduce((total, member) => total + member.capacity, 0);
  const remainingCapacity = allowedJobsCount - assignedCapacity;
  const isValid = members.length === 0 || assignedCapacity === allowedJobsCount;
  const isDirty = JSON.stringify(members) !== JSON.stringify(initialMembers);
  const availableUsers = selectableUsers.filter((user) => !members.some((member) => member.belongsToUserId === user._id));

  const setCapacity = (belongsToUserId: string, capacity: number) => {
    setMembers((previous) =>
      previous.map((member) =>
        member.belongsToUserId === belongsToUserId ? { ...member, capacity: Math.max(0, Math.min(allowedJobsCount, capacity)) } : member
      )
    );
  };

  const addMember = (belongsToUserId: string) => {
    const user = selectableUsers.find((selectableUser) => selectableUser._id === belongsToUserId);
    if (!user) return;

    setMembers((previous) => [...previous, { belongsToUserId: user._id, fullName: user.value, capacity: 0 }]);
  };

  const removeMember = (belongsToUserId: string) => {
    setMembers((previous) => previous.filter((member) => member.belongsToUserId !== belongsToUserId));
  };

  return (
    <div className="flex flex-col gap-4">
      <AllocationMeter members={members} allowedJobsCount={allowedJobsCount} assignedCapacity={assignedCapacity} remainingCapacity={remainingCapacity} />

      <div className="flex flex-col gap-[10px]">
        {members.length === 0 && <p className="text-[13px] text-[#888]">Noch niemand zugewiesen — unten einen Mitarbeiter hinzufügen.</p>}
        {members.map((member, index) => (
          <MemberRow
            key={member.belongsToUserId}
            member={member}
            color={getMemberColor(index)}
            allowedJobsCount={allowedJobsCount}
            onCapacityChange={(capacity) => setCapacity(member.belongsToUserId, capacity)}
            onRemove={() => removeMember(member.belongsToUserId)}
          />
        ))}
      </div>

      <Select value="" onValueChange={addMember} disabled={availableUsers.length === 0}>
        <SelectTrigger className="h-10 w-full max-w-[320px] rounded-lg border border-[#D0D5DD] px-3 text-sm" selectIconVariant="styled">
          <SelectValue placeholder={availableUsers.length === 0 ? 'Alle Mitarbeiter zugewiesen' : 'Mitarbeiter hinzufügen …'} />
        </SelectTrigger>
        <SelectContent>
          {availableUsers.map((user) => (
            <SelectItem key={user._id} value={user._id}>
              {user.value}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div className="flex flex-wrap items-center justify-between gap-4 border-t border-[#E4E7EC] pt-4">
        {isValid ? (
          <p className="text-[13px] text-[#888]">Anteil 0 bedeutet Vertretung: erhält Benachrichtigungen, aber keine Provision.</p>
        ) : (
          <p className="text-[13px] font-semibold text-[#CE0909]">
            Die Anteile müssen zusammen genau {allowedJobsCount} ergeben — aktuell sind es {assignedCapacity}.
          </p>
        )}
        <div className="flex gap-2">
          <Button variant="outline" disabled={!isDirty || isSaving} onClick={() => setMembers(initialMembers)}>
            Verwerfen
          </Button>
          <Button disabled={!isValid || !isDirty || isSaving} onClick={() => onSave(members)}>
            Speichern
          </Button>
        </div>
      </div>
    </div>
  );
};

const AllocationMeter = ({
  members,
  allowedJobsCount,
  assignedCapacity,
  remainingCapacity
}: {
  members: AssignedInternalUserResponseDto[];
  allowedJobsCount: number;
  assignedCapacity: number;
  remainingCapacity: number;
}) => {
  return (
    <div className="flex flex-col gap-[9px]">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <p className="text-[15px] font-bold text-[#12282A]">
          {assignedCapacity} <span className="font-medium text-[#888]">von {allowedJobsCount} Paketen verteilt</span>
        </p>
        <AllocationStatusChip memberCount={members.length} remainingCapacity={remainingCapacity} />
      </div>

      <div className="flex h-3 gap-[2px] overflow-hidden rounded-full bg-[#EEF1F4]">
        {members
          .filter((member) => member.capacity > 0)
          .map((member) => (
            <span
              key={member.belongsToUserId}
              style={{ flexGrow: member.capacity, backgroundColor: getMemberColor(members.indexOf(member)) }}
              className="block"
            />
          ))}
        {remainingCapacity > 0 && (
          <span
            style={{
              flexGrow: remainingCapacity,
              backgroundImage: 'repeating-linear-gradient(45deg, #E4E7EC 0 5px, #EEF1F4 5px 10px)'
            }}
            className="block"
          />
        )}
      </div>
    </div>
  );
};

const AllocationStatusChip = ({ memberCount, remainingCapacity }: { memberCount: number; remainingCapacity: number }) => {
  if (memberCount === 0) {
    return <span className="rounded-full border border-[#E4E7EC] bg-[#F3F5F7] px-[11px] py-1 text-[12px] font-bold text-[#888]">Niemand zugewiesen</span>;
  }

  if (remainingCapacity === 0) {
    return (
      <span className="rounded-full border border-[#C0DE60] bg-[#C0DE60]/[0.16] px-[11px] py-1 text-[12px] font-bold text-[#5F7418]">
        Vollständig verteilt
      </span>
    );
  }

  return (
    <span className="rounded-full border border-[#CE0909]/30 bg-[#CE0909]/[0.08] px-[11px] py-1 text-[12px] font-bold text-[#CE0909]">
      {remainingCapacity > 0 ? `${remainingCapacity} noch offen` : `${Math.abs(remainingCapacity)} zu viel`}
    </span>
  );
};

const MemberRow = ({
  member,
  color,
  allowedJobsCount,
  onCapacityChange,
  onRemove
}: {
  member: AssignedInternalUserResponseDto;
  color: string;
  allowedJobsCount: number;
  onCapacityChange: (capacity: number) => void;
  onRemove: () => void;
}) => {
  const isRepresentative = member.capacity === 0;

  return (
    <div
      className={`flex items-center gap-[14px] rounded-xl border border-[#E4E7EC] px-[13px] py-[11px] ${
        isRepresentative ? 'border-dashed bg-[#F7FAFB]' : 'bg-[#FCFDFD]'
      }`}>
      <span
        style={{ backgroundColor: color }}
        className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full text-[12px] font-bold text-[#12282A]">
        {getInitials(member.fullName)}
      </span>

      <span className="flex min-w-0 flex-1 flex-col">
        <strong className="text-[14.5px] font-semibold text-[#12282A]">{member.fullName}</strong>
        <small className="text-[12px] text-[#888]">
          {isRepresentative ? 'Vertretung · nur Benachrichtigungen, keine Provision' : `${member.capacity} von ${allowedJobsCount} Paketen`}
        </small>
      </span>

      <span className="flex shrink-0 items-center gap-[2px] rounded-lg border border-[#D0D5DD] bg-white p-[2px]">
        <button
          type="button"
          aria-label="Anteil verringern"
          disabled={member.capacity <= 0}
          onClick={() => onCapacityChange(member.capacity - 1)}
          className="h-7 w-7 rounded-md text-[17px] leading-none text-[#12282A] hover:bg-[#EEF4F5] disabled:opacity-30">
          −
        </button>
        <span className="min-w-[30px] text-center text-[14.5px] font-bold tabular-nums text-[#12282A]">{member.capacity}</span>
        <button
          type="button"
          aria-label="Anteil erhöhen"
          disabled={member.capacity >= allowedJobsCount}
          onClick={() => onCapacityChange(member.capacity + 1)}
          className="h-7 w-7 rounded-md text-[17px] leading-none text-[#12282A] hover:bg-[#EEF4F5] disabled:opacity-30">
          +
        </button>
      </span>

      <button
        type="button"
        aria-label={`${member.fullName} entfernen`}
        onClick={onRemove}
        className="h-[30px] w-[30px] shrink-0 rounded-md text-[16px] leading-none text-[#98A2B3] hover:bg-[#CE0909]/[0.06] hover:text-[#CE0909]">
        ✕
      </button>
    </div>
  );
};

export default AssignmentMembersEditor;
